<template>
  <div>
    <cc-collapse :title="$t('ct-instagram-account')">
      <div v-if="connectionLoading" class="form-group text-muted">
        {{ $t('ct-loading') }}...
      </div>
      <div v-else-if="connected" class="form-group">
        <p class="mb-2" v-if="connectionUsername">@{{ connectionUsername }}</p>
        <button type="button" class="btn btn-sm btn-outline-danger" @click.prevent="disconnectAccount">
          {{ $t('ct-instagram-disconnect') }}
        </button>
      </div>
      <div v-else class="form-group text-muted">
        {{ $t('ct-instagram-connect-from-element') }}
      </div>
    </cc-collapse>

    <cc-collapse :title="$t('ct-general-settings')">
      <div class="form-group">
        <label>{{ $t('ct-instagram-layout') }}</label>
        <div class="ig-layout-picker">
          <button
            v-for="layout in layoutOptions"
            :key="layout.value"
            type="button"
            class="ig-layout-picker__item"
            :class="{ 'is-active': selectedLayoutFamily === layout.value }"
            @click="selectLayout(layout.value)"
          >
            <span class="ig-layout-icon" :class="'ig-layout-icon--' + layout.value" aria-hidden="true">
              <span v-for="n in layout.cells" :key="n"></span>
            </span>
            <span class="ig-layout-picker__label">{{ layout.label }}</span>
          </button>
        </div>
      </div>
      <cc-slider-component ref="postsPerPageConfig" />
      <cc-slider-component ref="columnsConfig" />
      <div class="form-group">
        <label for="ig-post-order">{{ $t('ct-instagram-post-order') }}</label>
        <select id="ig-post-order" v-model="config.postOrder" class="form-control" @change="save()">
          <option value="random">{{ $t('ct-instagram-order-random') }}</option>
          <option value="newest">{{ $t('ct-instagram-order-newest') }}</option>
          <option value="oldest">{{ $t('ct-instagram-order-oldest') }}</option>
        </select>
      </div>
    </cc-collapse>

    <cc-collapse :title="$t('ct-sizes')">
      <cc-slider-component ref="feedWidthConfig" />
      <cc-slider-component ref="feedHeightConfig" />
      <cc-slider-component ref="feedHeaderHeightConfig" />
      <cc-slider-component ref="postHeightConfig" />
      <cc-slider-component ref="maxWidthConfig" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-background-settings')">
      <cc-box-color ref="backgroundColor" />
      <cc-image-component ref="backgroundImage" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-border')">
      <cc-border-component ref="border" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-radius')">
      <cc-border-radius-component ref="borderRadius" />
      <cc-slider-component ref="imageBorderRadiusConfig" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-shadow')">
      <cc-shadow ref="shadow" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-instagram-feed-header')">
      <div class="form-group">
        <div class="form-check">
          <input id="ig-show-header" v-model="config.showHeader" class="form-check-input" type="checkbox" @change="save()" />
          <label class="form-check-label" for="ig-show-header">{{ $t('ct-instagram-show-header') }}</label>
        </div>
      </div>
      <div class="form-group">
        <label for="ig-header-type">{{ $t('ct-instagram-header-type') }}</label>
        <select id="ig-header-type" v-model="config.headerType" class="form-control" @change="save()">
          <option value="header1">{{ $t('ct-instagram-header-1') }}</option>
          <option value="header2">{{ $t('ct-instagram-header-2') }}</option>
        </select>
      </div>
      <div class="form-group">
        <label for="ig-follow-text">{{ $t('ct-instagram-primary-text') }}</label>
        <input id="ig-follow-text" v-model="config.followButtonText" class="form-control" type="text" maxlength="80" @change="save()" />
      </div>
      <text-style-component ref="followButtonTextStyle" />
      <cc-slider-component ref="followButtonWidthConfig" />
      <cc-slider-component ref="followButtonHeightConfig" />
      <cc-border-radius-component ref="followButtonBorderRadius" />
      <icon-component ref="followButtonIcon" />
      <cc-box-color ref="followButtonBackground" />
      <cc-image-component ref="followButtonBackgroundImage" />
      <div class="form-group">
        <div class="form-check">
          <input id="ig-follow-hover" v-model="config.followButtonHoverEnabled" class="form-check-input" type="checkbox" @change="save()" />
          <label class="form-check-label" for="ig-follow-hover">{{ $t('ct-instagram-hover-effect') }}</label>
        </div>
      </div>
      <cc-box-color v-show="config.followButtonHoverEnabled" ref="followButtonHoverBackground" />
      <cc-box-color v-show="config.followButtonHoverEnabled" ref="followButtonHoverColor" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-button')">
      <div class="form-group">
        <div class="form-check">
          <input id="ig-show-load-more" v-model="config.showLoadMore" class="form-check-input" type="checkbox" @change="save()" />
          <label class="form-check-label" for="ig-show-load-more">{{ $t('ct-instagram-show-button') }}</label>
        </div>
      </div>
      <div class="form-group">
        <label for="ig-load-more-text">{{ $t('ct-instagram-primary-text') }}</label>
        <input id="ig-load-more-text" v-model="config.loadMoreText" class="form-control" type="text" maxlength="80" @change="save()" />
      </div>
      <text-style-component ref="loadMoreTextStyle" />
      <cc-slider-component ref="loadMoreButtonWidthConfig" />
      <cc-slider-component ref="loadMoreButtonHeightConfig" />
      <cc-border-radius-component ref="loadMoreBorderRadius" />
      <icon-component ref="loadMoreIcon" />
      <cc-box-color ref="loadMoreBackground" />
      <cc-image-component ref="loadMoreBackgroundImage" />
      <div class="form-group">
        <div class="form-check">
          <input id="ig-load-more-hover" v-model="config.loadMoreHoverEnabled" class="form-check-input" type="checkbox" @change="save()" />
          <label class="form-check-label" for="ig-load-more-hover">{{ $t('ct-resource-hover') }}</label>
        </div>
      </div>
      <cc-box-color v-show="config.loadMoreHoverEnabled" ref="loadMoreHoverBackground" />
      <cc-box-color v-show="config.loadMoreHoverEnabled" ref="loadMoreHoverColor" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-instagram-likes-comments')">
      <div class="form-group">
        <div class="form-check">
          <input id="ig-show-engagement" v-model="config.showEngagement" class="form-check-input" type="checkbox" @change="setShowEngagement" />
          <label class="form-check-label" for="ig-show-engagement">{{ $t('ct-instagram-show-engagement') }}</label>
        </div>
      </div>
      <div class="form-group">
        <div class="form-check">
          <input id="ig-show-likes" v-model="config.showLikes" class="form-check-input" type="checkbox" @change="save()" />
          <label class="form-check-label" for="ig-show-likes">{{ $t('ct-instagram-show-likes-icon') }}</label>
        </div>
      </div>
      <cc-box-color ref="likesIconColor" />
      <div class="form-group">
        <div class="form-check">
          <input id="ig-show-comments" v-model="config.showComments" class="form-check-input" type="checkbox" @change="save()" />
          <label class="form-check-label" for="ig-show-comments">{{ $t('ct-instagram-show-comments-icon') }}</label>
        </div>
      </div>
      <cc-box-color ref="commentsIconColor" />
      <cc-slider-component ref="engagementIconSizeConfig" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-instagram-filter')">
      <div class="form-group">
        <label>{{ $t('ct-instagram-filter-include') }}</label>
        <hashtag-tag-input
          :value="config.filterIncludeHashtags"
          :placeholder="$t('ct-instagram-hashtag-placeholder')"
          @input="setFilterInclude"
        />
      </div>
      <div class="form-group">
        <label>{{ $t('ct-instagram-filter-exclude') }}</label>
        <hashtag-tag-input
          :value="config.filterExcludeHashtags"
          :placeholder="$t('ct-instagram-hashtag-placeholder')"
          @input="setFilterExclude"
        />
      </div>
    </cc-collapse>

    <cc-collapse :title="$t('ct-animation')">
      <cc-animation ref="animation" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-tag-config')">
      <cc-tag ref="tag" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-layers')">
      <cc-z-index ref="zIndex" />
    </cc-collapse>

    <cc-collapse :title="$t('ct-padding')" class="padding-mobile">
      <cc-padding ref="padding" />
    </cc-collapse>
  </div>
</template>

<script>
import TextStyleComponent from '../../resources/TextStyle.vue';
import IconComponent from '../../resources/Icon.vue';
import HashtagTagInput from './HashtagTagInput.vue';
import { applyFeedLayoutDefaults, resolveLayoutFamily } from './layoutPresets';
import {
  disconnect,
  getConnection,
} from '../../../../js/services/integrations-internas/instagramFeed';

function createDefaultTextStyle(overrides) {
  return Object.assign({
    family: 'Arial, Helvetica, sans-serif',
    size: '14px',
    color: '#FFFFFF',
    align: 'center',
    weight: '600',
    style: 'initial',
    stroke: 'none',
  }, overrides || {});
}

export default {
  name: 'Options',
  components: {
    TextStyleComponent,
    IconComponent,
    HashtagTagInput,
  },
  data() {
    return {
      config: {},
      callback() {},
      connected: false,
      connectionLoading: false,
      connectionUsername: '',
      website: null,
      layoutOptions: [
        { value: 'grid', label: this.$t('ct-grid'), cells: 9 },
        { value: 'slider', label: this.$t('ct-instagram-layout-slider'), cells: 4 },
        { value: 'list', label: this.$t('ct-instagram-layout-list'), cells: 3 },
        { value: 'masonry', label: this.$t('ct-instagram-layout-masonry'), cells: 6 },
      ],
    };
  },
  computed: {
    selectedLayoutFamily() {
      return resolveLayoutFamily(this.config.feedLayout);
    },
  },
  beforeDestroy() {
    this.$root.$off('instagram-feed-connected', this.onFeedConnected);
  },
  mounted() {
    this.$root.$on('instagram-feed-connected', this.onFeedConnected);
  },
  methods: {
    init(config, callback) {
      this.config = this.normalizeConfig(_.clone(config));
      this.callback = callback;
      this.website = this.resolveWebsite();
      this.bootstrapConnectionStatus();
      this.initStandardControls();
    },
    normalizeConfig(config) {
      if (!config.filterIncludeHashtags) {
        config.filterIncludeHashtags = [];
      }
      if (!config.filterExcludeHashtags) {
        config.filterExcludeHashtags = [];
      }
      if (!config.followButtonTextStyle) {
        config.followButtonTextStyle = createDefaultTextStyle({
          color: config.followButtonColor || '#FFFFFF',
        });
      }
      if (!config.loadMoreTextStyle) {
        config.loadMoreTextStyle = createDefaultTextStyle({
          color: config.loadMoreColor || '#262626',
        });
      }
      if (!config.followButtonBorderRadius) {
        config.followButtonBorderRadius = '8px 8px 8px 8px';
      }
      if (!config.loadMoreBorderRadius) {
        config.loadMoreBorderRadius = '8px 8px 8px 8px';
      }
      if (typeof config.loadMoreHoverEnabled === 'undefined') {
        config.loadMoreHoverEnabled = false;
      }
      if (!config.animation) {
        config.animation = { in: {}, out: {} };
      }
      if (!config.animation.in) {
        config.animation.in = {};
      }
      if (!config.feedLayout) {
        config.feedLayout = 'grid';
      }
      if (!config.sliderVisibleCount) {
        config.sliderVisibleCount = 4;
      }
      if (!config.postOrder) {
        config.postOrder = 'newest';
      }
      if (typeof config.hoverEffectsEnabled === 'undefined') {
        config.hoverEffectsEnabled = config.showEngagement !== false;
      }
      if (typeof config.showEngagement === 'undefined') {
        config.showEngagement = config.hoverEffectsEnabled !== false;
      }
      return config;
    },
    initStandardControls() {
      this.setSlider('feedWidthConfig', this.config.width, 320, 1400, 'feed-width', this.setFeedWidth);
      this.setSlider('feedHeightConfig', this.config.height, 200, 1200, 'feed-height', this.setFeedHeight);
      this.setSlider('feedHeaderHeightConfig', this.config.feedHeaderHeight, 48, 200, 'feed-header-height', this.setFeedHeaderHeight);
      this.setSlider('postHeightConfig', this.config.postHeight, 120, 400, 'post-height', this.setPostHeight);
      this.setSlider('maxWidthConfig', this.config.maxWidth, 320, 1400, 'max-width', this.setMaxWidth);
      this.setSlider('columnsConfig', this.config.columns, 1, 6, 'number-of-columns', this.setColumns, true);
      this.setSlider('postsPerPageConfig', this.config.postsPerPage, 1, 30, 'number-of-posts', this.setPostsPerPage, true);
      this.setSlider('imageBorderRadiusConfig', this.config.imageBorderRadius, 0, 40, 'card-radius', this.setImageBorderRadius, true);
      this.setSlider('followButtonWidthConfig', this.config.followButtonWidth, 80, 320, 'button-width', this.setFollowButtonWidth);
      this.setSlider('followButtonHeightConfig', this.config.followButtonHeight, 28, 80, 'button-height', this.setFollowButtonHeight);
      this.setSlider('loadMoreButtonWidthConfig', this.config.loadMoreButtonWidth, 160, 640, 'button-width', this.setLoadMoreButtonWidth);
      this.setSlider('loadMoreButtonHeightConfig', this.config.loadMoreButtonHeight, 28, 80, 'button-height', this.setLoadMoreButtonHeight);
      this.setSlider('engagementIconSizeConfig', this.config.engagementIconSize, 10, 24, 'icon-size', this.setEngagementIconSize, true);

      this.setColor('followButtonBackground', this.config.followButtonBackground, this.setFollowButtonBackground);
      this.setColor('followButtonHoverBackground', this.config.followButtonHoverBackground, this.setFollowButtonHoverBackground);
      this.setColor('followButtonHoverColor', this.config.followButtonHoverColor, this.setFollowButtonHoverColor);
      this.setColor('loadMoreBackground', this.config.loadMoreBackground, this.setLoadMoreBackground);
      this.setColor('loadMoreHoverBackground', this.config.loadMoreHoverBackground, this.setLoadMoreHoverBackground);
      this.setColor('loadMoreHoverColor', this.config.loadMoreHoverColor, this.setLoadMoreHoverColor);
      this.setColor('likesIconColor', this.config.likesIconColor, this.setLikesIconColor);
      this.setColor('commentsIconColor', this.config.commentsIconColor, this.setCommentsIconColor);

      this.initTextStyle('followButtonTextStyle', this.config.followButtonTextStyle, this.setFollowButtonTextStyle);
      this.initTextStyle('loadMoreTextStyle', this.config.loadMoreTextStyle, this.setLoadMoreTextStyle);

      this.$refs.followButtonBorderRadius.setValues({
        defaultValue: this.config.followButtonBorderRadius,
        labelDesc: 'input-radius',
        name: 'followButtonBorderRadius',
        callback: this.setFollowButtonBorderRadius,
      });
      this.$refs.loadMoreBorderRadius.setValues({
        defaultValue: this.config.loadMoreBorderRadius,
        labelDesc: 'input-radius',
        name: 'loadMoreBorderRadius',
        callback: this.setLoadMoreBorderRadius,
      });

      this.$refs.followButtonIcon.setValues({
        defaultIcon: this.config.followButtonIcon,
        defaultPosition: this.config.followButtonIconPosition || 'left',
        labelDesc: 'icon',
        canRemove: true,
        callback: this.setFollowButtonIcon,
      });
      this.$refs.loadMoreIcon.setValues({
        defaultIcon: this.config.loadMoreIcon,
        defaultPosition: this.config.loadMoreIconPosition || 'left',
        labelDesc: 'icon',
        canRemove: true,
        callback: this.setLoadMoreIcon,
      });

      this.$refs.border.setValues({
        defaultValue: this.config.border,
        labelDesc: 'border',
        name: 'border',
        callback: this.setBorder,
      });
      this.$refs.borderRadius.setValues({
        defaultValue: this.config.borderRadius,
        labelDesc: 'borderRadius',
        name: 'borderRadius',
        callback: this.setBorderRadius,
      });
      this.$refs.shadow.setValues({
        defaultValue: this.config.boxShadow,
        labelDesc: 'shadow',
        name: 'shadow',
        callback: this.setShadow,
      });
      this.$refs.backgroundColor.setValues({
        defaultValue: this.config.backgroundColor,
        gradient: true,
        labelDesc: 'bg-color',
        name: 'backgroundColor',
        callback: this.setBackgroundColor,
      });
      this.$refs.backgroundImage.setValues({
        defaultValue: this.config.backgroundImage,
        defaultValueOrigin: this.config.backgroundOrigin,
        defaultValueRepeat: this.config.backgroundRepeat,
        defaultValueSize: this.config.backgroundSize,
        defaultValueAttachment: this.config.backgroundAttachment,
        defaultValuePosition: this.config.backgroundPosition,
        canExternal: true,
        canRemove: true,
        callback: this.setBackgroundImage,
      });
      this.$refs.followButtonBackgroundImage.setValues({
        defaultValue: this.config.followButtonBackgroundImage,
        defaultValueOrigin: this.config.followButtonBackgroundOrigin,
        defaultValueRepeat: this.config.followButtonBackgroundRepeat,
        defaultValueSize: this.config.followButtonBackgroundSize,
        defaultValuePosition: this.config.followButtonBackgroundPosition,
        canExternal: true,
        canRemove: true,
        labelDesc: 'bg-image',
        callback: this.setFollowButtonBackgroundImage,
      });
      this.$refs.loadMoreBackgroundImage.setValues({
        defaultValue: this.config.loadMoreBackgroundImage,
        defaultValueOrigin: this.config.loadMoreBackgroundOrigin,
        defaultValueRepeat: this.config.loadMoreBackgroundRepeat,
        defaultValueSize: this.config.loadMoreBackgroundSize,
        defaultValuePosition: this.config.loadMoreBackgroundPosition,
        canExternal: true,
        canRemove: true,
        labelDesc: 'bg-image',
        callback: this.setLoadMoreBackgroundImage,
      });
      this.$refs.animation.setValues({
        action: this.config.animation.in.action,
        effect: this.config.animation.in.effect,
        scroll: this.config.animation.in.scroll,
        time: this.config.animation.in.time,
        speed: this.config.animation.in.speed,
        infinite: this.config.animation.in.infinite,
        labelDesc: 'animation',
        name: 'animate',
        callback: this.setAnimation,
      }, null);
      this.$refs.tag.setValues({ tag: this.config.tag, callback: this.setTag });
      this.$refs.zIndex.setValues({ defaultValue: this.config.zIndex, callback: this.setZIndex });
      this.$refs.padding.setValues({
        defaultTop: this.config.paddingTop,
        defaultBottom: this.config.paddingBottom,
        defaultLeft: this.config.paddingLeft,
        defaultRight: this.config.paddingRight,
        min: 0,
        max: 150,
        labelDesc: 'padding',
        callback: this.setPadding,
      });
    },
    initTextStyle(refName, textStyle, callback) {
      const style = textStyle || createDefaultTextStyle();
      this.$refs[refName].setValues({
        name: refName,
        defaultFamily: style.family,
        defaultSize: parseInt(style.size, 10) || 14,
        defaultColor: style.color,
        defaultAlign: style.align,
        defaultWeight: style.weight,
        defaultStyle: style.style,
        defaultStroke: style.stroke,
        shitOptions: true,
        showSizeMobile: false,
        labelDesc: 'font-style-button',
        callback,
      }, 'option-button');
    },
    setSlider(refName, value, min, max, labelDesc, callback, isNumber) {
      this.$refs[refName].setValues({
        defaultValue: isNumber ? (parseInt(value, 10) || min) : this.parsePx(value, min),
        defaultValueChecked: true,
        min,
        max,
        canBeCheckbox: false,
        labelDesc,
        name: refName,
        callback,
      });
    },
    setColor(refName, value, callback) {
      this.$refs[refName].setValues({
        defaultValue: value,
        gradient: refName.indexOf('Hover') === -1 && (refName === 'followButtonBackground' || refName === 'loadMoreBackground'),
        labelDesc: 'resource-bg-color',
        name: refName,
        callback,
      });
    },
    parsePx(value, fallback) {
      if (value === undefined || value === null || value === '' || value === 'auto') {
        return fallback;
      }
      const parsed = parseInt(String(value).replace('px', ''), 10);
      return Number.isNaN(parsed) ? fallback : parsed;
    },
    setFilterInclude(tags) {
      this.config.filterIncludeHashtags = tags;
      this.save();
    },
    setFilterExclude(tags) {
      this.config.filterExcludeHashtags = tags;
      this.save();
    },
    resolveWebsite() {
      const storeWebsite = this.$store && this.$store.getters ? this.$store.getters.website : null;
      if (storeWebsite && (storeWebsite.encrypted_id || storeWebsite.id)) {
        return storeWebsite.encrypted_id || storeWebsite.id;
      }
      let parent = this.$parent;
      while (parent) {
        if (parent.$data && parent.$data.id_website) {
          return parent.$data.id_website;
        }
        parent = parent.$parent;
      }
      if (this.$route && this.$route.params && this.$route.params.website_id) {
        return this.$route.params.website_id;
      }
      return null;
    },
    bootstrapConnectionStatus() {
      if (!this.website) {
        this.connected = false;
        return;
      }
      this.connectionLoading = true;
      getConnection(this.website)
        .then((res) => {
          this.connected = !!(res.data && res.data.connected);
          this.connectionUsername = res.data && res.data.connection ? res.data.connection.username : '';
        })
        .catch(() => {
          this.connected = false;
          this.connectionUsername = '';
        })
        .finally(() => {
          this.connectionLoading = false;
        });
    },
    onFeedConnected() {
      this.bootstrapConnectionStatus();
    },
    disconnectAccount() {
      if (!this.website) {
        this.website = this.resolveWebsite();
      }
      if (!this.website) {
        return;
      }
      disconnect(this.website).then(() => {
        this.connected = false;
        this.connectionUsername = '';
        this.$root.$emit('instagram-feed-disconnected');
      });
    },
    applyTextStylePayload(payload) {
      return {
        family: payload.family,
        size: `${payload.size}px`,
        color: payload.color,
        align: payload.align,
        weight: payload.weight,
        style: payload.style,
        stroke: payload.stroke,
      };
    },
    setFeedWidth(value) { this.config.width = `${value}px`; this.save(); },
    setFeedHeight(value) {
      this.config.height = `${value}px`;
      this.config.minHeight = `${value}px`;
      this.save();
    },
    setFeedHeaderHeight(value) { this.config.feedHeaderHeight = `${value}px`; this.save(); },
    setPostHeight(value) { this.config.postHeight = `${value}px`; this.save(); },
    setMaxWidth(value) { this.config.maxWidth = `${value}px`; this.save(); },
    setColumns(value) {
      this.config.columns = parseInt(value, 10) || 3;
      if (resolveLayoutFamily(this.config.feedLayout) === 'slider') {
        this.config.sliderVisibleCount = this.config.columns;
      }
      this.save();
    },
    setPostsPerPage(value) { this.config.postsPerPage = parseInt(value, 10) || 9; this.save(); },
    selectLayout(family) {
      this.config.feedLayout = family;
      const updated = applyFeedLayoutDefaults(family, this.config);
      Object.assign(this.config, updated);
      this.save();
    },
    setShowEngagement() {
      this.config.hoverEffectsEnabled = !!this.config.showEngagement;
      this.save();
    },
    setImageBorderRadius(value) { this.config.imageBorderRadius = parseInt(value, 10) || 0; this.save(); },
    setFollowButtonWidth(value) { this.config.followButtonWidth = `${value}px`; this.save(); },
    setFollowButtonHeight(value) { this.config.followButtonHeight = `${value}px`; this.save(); },
    setLoadMoreButtonWidth(value) { this.config.loadMoreButtonWidth = `${value}px`; this.save(); },
    setLoadMoreButtonHeight(value) { this.config.loadMoreButtonHeight = `${value}px`; this.save(); },
    setEngagementIconSize(value) { this.config.engagementIconSize = parseInt(value, 10) || 14; this.save(); },
    setFollowButtonBackground(value) { this.config.followButtonBackground = value; this.save(); },
    setFollowButtonHoverBackground(value) { this.config.followButtonHoverBackground = value; this.save(); },
    setFollowButtonHoverColor(value) { this.config.followButtonHoverColor = value; this.save(); },
    setLoadMoreBackground(value) { this.config.loadMoreBackground = value; this.save(); },
    setLoadMoreHoverBackground(value) { this.config.loadMoreHoverBackground = value; this.save(); },
    setLoadMoreHoverColor(value) { this.config.loadMoreHoverColor = value; this.save(); },
    setLikesIconColor(value) { this.config.likesIconColor = value; this.save(); },
    setCommentsIconColor(value) { this.config.commentsIconColor = value; this.save(); },
    setFollowButtonTextStyle(payload) {
      this.config.followButtonTextStyle = this.applyTextStylePayload(payload);
      this.config.followButtonColor = payload.color;
      this.save();
    },
    setLoadMoreTextStyle(payload) {
      this.config.loadMoreTextStyle = this.applyTextStylePayload(payload);
      this.config.loadMoreColor = payload.color;
      this.save();
    },
    setFollowButtonBorderRadius(data) {
      this.config.followButtonBorderRadius = data.value;
      this.save();
    },
    setLoadMoreBorderRadius(data) {
      this.config.loadMoreBorderRadius = data.value;
      this.save();
    },
    setFollowButtonIcon() {
      this.config.followButtonIcon = this.$refs.followButtonIcon.iconSelected || '';
      this.config.followButtonIconPosition = this.$refs.followButtonIcon.position || 'left';
      this.save();
    },
    setLoadMoreIcon() {
      this.config.loadMoreIcon = this.$refs.loadMoreIcon.iconSelected || '';
      this.config.loadMoreIconPosition = this.$refs.loadMoreIcon.position || 'left';
      this.save();
    },
    setBorder(data) { this.config.border = `${data.size}px ${data.style} ${data.color}`; this.save(); },
    setBorderRadius(data) { this.config.borderRadius = data.value; this.save(); },
    setShadow(data) {
      this.config.boxShadow = `${data.h}px ${data.v}px ${data.b}px ${data.s}px ${data.color} ${data.inset ? 'inset' : ''}`;
      this.save();
    },
    setBackgroundColor(value) { this.config.backgroundColor = value; this.save(); },
    setBackgroundImage(data) {
      if (data.type === 'setImage') {
        this.config.backgroundColor = 'white';
        this.config.backgroundImage = data.src;
      }
      if (data.type === 'removeImage') {
        this.config.backgroundImage = 'none';
      }
      if (data.type === 'setConfig') {
        this.config.backgroundColor = 'white';
        this.config.backgroundPosition = data.position;
        this.config.backgroundSize = data.size;
        this.config.backgroundRepeat = data.repeat;
        this.config.backgroundOrigin = data.origin;
        this.config.backgroundAttachment = data.attachment;
      }
      this.save();
    },
    setFollowButtonBackgroundImage(data) {
      this.applyButtonBackgroundImage(data, 'followButton');
      this.save();
    },
    setLoadMoreBackgroundImage(data) {
      this.applyButtonBackgroundImage(data, 'loadMore');
      this.save();
    },
    applyButtonBackgroundImage(data, prefix) {
      if (data.type === 'setImage') {
        this.config[`${prefix}BackgroundImage`] = data.src;
      }
      if (data.type === 'removeImage') {
        this.config[`${prefix}BackgroundImage`] = 'none';
      }
      if (data.type === 'setConfig') {
        this.config[`${prefix}BackgroundPosition`] = data.position;
        this.config[`${prefix}BackgroundSize`] = data.size;
        this.config[`${prefix}BackgroundRepeat`] = data.repeat;
        this.config[`${prefix}BackgroundOrigin`] = data.origin;
      }
    },
    setAnimation(data) {
      this.config.animation.in = _.merge(this.config.animation.in, data);
      this.save();
    },
    setTag(value) { this.config.tag = value; this.save(); },
    setZIndex(value) { this.config.zIndex = value; this.save(); },
    setPadding(data) {
      this.config.paddingTop = `${data.paddingTop}px`;
      this.config.paddingBottom = `${data.paddingBottom}px`;
      this.config.paddingLeft = `${data.paddingLeft}px`;
      this.config.paddingRight = `${data.paddingRight}px`;
      this.save();
    },
    save() {
      if (typeof this.callback === 'function') {
        this.callback(this.config);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ig-layout-picker {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.ig-layout-picker__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 64px;
  padding: 8px 4px 6px;
  border: 1px solid #d5d9e2;
  border-radius: 8px;
  background: #fff;
  color: #6b7288;
  font: inherit;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  &:hover:not(.is-active) {
    border-color: #2d6df6;
    color: #2d6df6;
  }

  &.is-active {
    background: #2d6df6;
    border-color: #2d6df6;
    color: #fff;
  }
}

.ig-layout-picker__label {
  font-size: 11px;
  line-height: 1.2;
  font-weight: 500;
}

.ig-layout-icon {
  display: grid;
  gap: 2px;
  width: 22px;
  height: 16px;

  span {
    display: block;
    background: currentColor;
    border-radius: 1px;
  }
}

.ig-layout-icon--grid {
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
}

.ig-layout-icon--slider {
  width: 24px;
  height: 8px;
  grid-template-columns: repeat(4, 1fr);
}

.ig-layout-icon--list {
  width: 10px;
  height: 18px;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(3, 1fr);
}

.ig-layout-icon--masonry {
  width: 22px;
  height: 16px;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);

  span:nth-child(1) { grid-column: 1; grid-row: 1 / 3; }
  span:nth-child(2) { grid-column: 2; grid-row: 1; }
  span:nth-child(3) { grid-column: 3; grid-row: 1 / 3; }
  span:nth-child(4) { grid-column: 1; grid-row: 3; }
  span:nth-child(5) { grid-column: 2; grid-row: 2 / 4; }
  span:nth-child(6) { grid-column: 3; grid-row: 3; }
}
</style>



// WEBPACK FOOTER //
// src/components/editor/apps/InstagramFeed/Options.vue